import { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { listOrders, normalizeOrderView, orderSearchTooShort, ORDER_SEARCH_MIN_LENGTH, ORDER_VIEWS, type Order } from '../../api/orders'
import { ApiError } from '../../api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { FilterBar } from '@/components/FilterBar'
import { PageHeader } from '@/components/PageHeader'
import { PortalBadge } from '@/components/PortalBadge'
import { OrderStatusFlags } from '@/components/OrderStatusFlags'

// Extrahiert die reine DR-Nummer aus "DR_1969041_2026_01" → "1969041"
function drNumber(number: string): string {
  return number.replace(/^DR_/, '').replace(/_\d{4}_\d{2}$/, '')
}

function customerLabel(order: Order): string {
  const addr = order.addresses?.shipping ?? order.addresses?.billing
  if (addr?.firstname || addr?.lastname) {
    return [addr.firstname, addr.lastname].filter(Boolean).join(' ')
  }
  return order.customer?.username ?? order.customer?.email ?? '—'
}

function fmtEUR(n: string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '—'
  const num = Number(n)
  if (Number.isNaN(num)) return '—'
  return num.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

export default function OrderList() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const currentView = normalizeOrderView(searchParams.get('view'))
  const search = searchParams.get('q') ?? ''
  const [localSearch, setLocalSearch] = useState(search)
  // Seite steht in der Adresse, nicht im Komponenten-Zustand: sonst landet der Rückweg aus
  // einem Auftrag (oder ein Neuladen) immer auf Seite 1. Das Backend blättert per Seite.
  const pageParam = Number(searchParams.get('page'))
  const page = Number.isInteger(pageParam) && pageParam > 1 ? pageParam : 1

  // Ansicht oder Suche wechseln: neue Adresse ohne Seite → wieder Seite 1.
  function setView(view: string) {
    setSearchParams({ view, ...(search ? { q: search } : {}) })
  }

  function handleSearch(value: string) {
    setLocalSearch(value)
    setSearchParams({ view: currentView, ...(value ? { q: value } : {}) })
  }

  function setPage(next: number) {
    const p = new URLSearchParams(searchParams)
    p.set('view', currentView)
    if (next > 1) p.set('page', String(next))
    else p.delete('page')
    setSearchParams(p, { replace: true })
  }

  // Zu kurzer Suchbegriff: keine Anfrage, sonst stuende die ungefilterte Liste unter dem gefuellten Suchfeld.
  const searchTooShort = orderSearchTooShort(search)
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['orders', currentView, search, page],
    queryFn: () =>
      listOrders({ view: currentView, search: search || undefined, page }),
    staleTime: 30_000,
    enabled: !searchTooShort,
  })

  const columns: Column<Order>[] = [
    {
      key: 'number',
      header: 'Auftrag-Nr.',
      cell: (o) => (
        <div>
          <div className="font-mono text-sm text-primary font-semibold">
            #{drNumber(o.number)}
          </div>
          {o.external_order_id && (
            <div className="font-mono text-[11px] text-muted truncate max-w-[140px]">
              {o.external_order_id}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'date',
      header: 'Datum',
      cell: (o) =>
        o.date ? (
          <span className="text-xs text-ink-2 font-mono">
            {new Date(o.date).toLocaleString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'customer',
      header: 'Kunde',
      cell: (o) => {
        const name = customerLabel(o)
        const email = o.customer?.email
        return (
          <div className="min-w-0">
            <div className="text-sm text-ink truncate max-w-[200px]">{name}</div>
            {email && name !== email && (
              <div className="text-[11px] text-ink-2 truncate max-w-[200px]">{email}</div>
            )}
          </div>
        )
      },
    },
    {
      key: 'platform',
      header: 'Portal',
      cell: (o) => <PortalBadge portal={o.platform} />,
    },
    {
      key: 'lines',
      header: 'Pos.',
      className: 'text-right',
      cell: (o) =>
        o.lines?.length ? (
          <span className="font-mono text-sm">{o.lines.length}</span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'invoice_amount',
      header: 'Betrag',
      className: 'text-right font-mono font-semibold',
      cell: (o) => fmtEUR(o.invoice_amount),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (o) => <OrderStatusFlags status={o.status} compact />,
    },
    {
      key: 'shipping',
      header: 'Versand',
      cell: (o) => {
        if (!o.shipping?.deliverer && !o.shipping?.code) {
          return <span className="text-muted">—</span>
        }
        return (
          <div className="font-mono text-[11px]">
            <div className="text-ink-2">{o.shipping?.deliverer ?? '—'}</div>
            {o.shipping?.code && (
              <div className="text-muted truncate max-w-[140px]">{o.shipping.code}</div>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="p-6 space-y-4">
      <PageHeader title="Aufträge" />

      <FilterBar
        filters={ORDER_VIEWS}
        active={currentView}
        onChange={setView}
        search={localSearch}
        onSearch={handleSearch}
        placeholder="Auftragsnummer, E-Mail, Name…"
        variant="tabs"
      />

      <div>
        {isError && (
          <p className="text-sm text-coral mb-2">
            {error instanceof ApiError ? error.message : 'Fehler beim Laden'}
          </p>
        )}

        {searchTooShort ? (
          <p role="status" className="rounded-md border border-line bg-panel px-5 py-8 text-center text-sm text-ink-2">
            Bitte mindestens {ORDER_SEARCH_MIN_LENGTH} Zeichen eingeben – Auftragsnummer, E-Mail, Name oder SKU.
          </p>
        ) : (
          <div className="rounded-md border border-line bg-panel">
            <DataTable
              data={data?.orders ?? []}
              columns={columns}
              rowKey={(o) => o.id}
              onRowClick={(o) =>
                navigate(`/orders/${encodeURIComponent(o.number)}`, { state: { liste: `/orders${location.search}` } })
              }
              isLoading={isLoading}
              emptyMessage="Keine Aufträge in dieser Ansicht."
              pagination={data ? {
                hasMore: data.has_more,
                onNext: () => setPage(page + 1),
                onPrev: () => setPage(page - 1),
                canPrev: page > 1,
                count: data.orders.length,
              } : undefined}
            />
          </div>
        )}
      </div>
    </div>
  )
}
